import { createContext, ReactNode, useEffect, useState } from 'react';

import { getSizeConversion } from 'shared/services/sizeConversion';
import { IAuth } from 'store/types/authTypes';
import { IConvertSize } from 'shared/types/ConvertSize';
import { Loader } from 'shared/components/Loader';
import { startCase } from 'shared/utils/startCase';
import { t } from 'localization/utils/t';
import { useGenderLabel } from 'shared/hooks/useGenderLabel';
import { useStoreSelector } from 'store/hooks/storeHooks';

const TP = 'productTemplate.providers.SizeConversionProvider';

interface ISizeConversionProvider {
  children: ReactNode;
}

export const SizeConversionContext = createContext(null);

export const SizeConversionProvider = ({ children }: ISizeConversionProvider) => {
  const getGenderLabel = useGenderLabel();
  const { isFashionProduct, productType, singleGender, sizeBrand, sizeUnit } = useStoreSelector(
    (state) => state.pdp.productTemplate,
  );
  const isApparel = isFashionProduct || (productType && productType !== 'sneakers');
  const { user }: IAuth = useStoreSelector((state) => state.auth);
  const {
    convertedSize: userSize,
    sizeBrand: userSizeBrand,
    sizeGender: userSizeGender,
    convertedSizeUnit: userSizeUnit,
  } = user || {};

  const [convertsToTitle, setConvertsToTitle] = useState<string>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [convertedUserSize, setConvertedUserSize] = useState<IConvertSize>(null);

  useEffect(() => {
    const getConvertedSize = async () => {
      try {
        setIsLoading(true);
        let convertedSize: IConvertSize;
        const userSizePreference = String(userSize);
        const fromBrand = sizeBrand; // bug in backend userSizeBrand || 'default';
        const toBrand = sizeBrand;
        const fromGender = userSizeGender?.toUpperCase();
        const toGender = singleGender?.toUpperCase();
        const fromUnit = userSizeUnit?.toUpperCase();
        const toUnit = sizeUnit?.toUpperCase();

        /* There is a bug in the API for some sizes (ex Adidas W4.5 US will convert to W4) */
        if (fromBrand === toBrand && fromGender === toGender && fromUnit === toUnit) {
          convertedSize = { size: userSizePreference, unit: fromUnit, gender: fromGender };
          setConvertedUserSize(convertedSize);
        } else {
          const result: IConvertSize = await getSizeConversion({
            userSize: userSizePreference,
            fromBrand,
            fromGender,
            toGender,
            fromUnit,
            toUnit,
            productType: 'SNEAKERS',
          });

          if (!result) throw new Error('Size conversion failed');

          convertedSize = result;
          setConvertedUserSize(convertedSize);
        }

        if (
          convertedSize?.unit?.toUpperCase() !== fromUnit ||
          convertedSize?.gender?.toUpperCase() !== fromGender ||
          convertedSize?.size !== userSizePreference
        ) {
          const userTitle = t(`${TP}.userTitle`, '{sizeUnit} {gender} Size {size}', {
            sizeUnit: userSizeUnit.toUpperCase(),
            gender: startCase(getGenderLabel(userSizeGender), {
              capitalizeInOtherLanguages: false,
            }),
            size: userSizePreference,
          });
          const convertedTitle = t(`${TP}.convertedTitle`, '{sizeUnit} {gender} Size {size}', {
            sizeUnit: convertedSize?.unit?.toUpperCase(),
            gender: startCase(getGenderLabel(convertedSize?.gender), {
              capitalizeInOtherLanguages: false,
            }),
            size: convertedSize?.size,
          });
          setConvertsToTitle(
            t(`${TP}.setConvertesToTitle`, '{userTitle} converts to {convertedTitle}', {
              userTitle,
              convertedTitle,
            }),
          );
        }
      } catch {
        setConvertedUserSize(null);
        setConvertsToTitle(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isApparel && userSizeGender && userSizeUnit && singleGender && sizeBrand && sizeUnit) {
      void getConvertedSize();
    }

    return () => {
      setIsLoading(false);
    };
  }, [
    getGenderLabel,
    isApparel,
    singleGender,
    sizeBrand,
    sizeUnit,
    userSize,
    userSizeBrand,
    userSizeGender,
    userSizeUnit,
  ]);

  const convertsToSizeDisplay = isLoading ? <Loader /> : convertsToTitle;

  const values = { convertedUserSize, convertsToSizeDisplay, isLoading };

  return <SizeConversionContext.Provider value={values}>{children}</SizeConversionContext.Provider>;
};
